import { NodonEnvironment } from '../models/environment.model';
import { guards } from './typeguards.helpers';

/**
 * Report PDF generation (PrintView + Browserless).
 *
 * Cloud Chrome cannot `page.goto` `http://localhost`. On localhost, export
 * uses a public tunnel (`npm run browserless:tunnel`) and the same hosted
 * Browserless `/function` API as test and production.
 *
 * Time budgets: Print wait (pages + cover-image settle + Mapbox idle +
 * chart settle + Map3d unmount) must stay under the ready-selector timeout.
 * Page and cover-image waits return early once ready, so Mapbox can use
 * {@link REPORT_PDF_COVER_MAP_WAIT_MS} on localhost. Hosted `/function` is
 * capped at {@link BROWSERLESS_HOSTED_MAX_TIMEOUT_MS} (1 minute on the current
 * plan). Goto + ready-selector + `page.pdf()` must fit in that session.
 * Test and production keep longer PrintView / Puppeteer waits so Mapbox covers
 * can idle; {@link getReportPdfBrowserlessFunctionTimeoutMs} follows that budget
 * and retries if the account max is lower.
 */

/** Hosted Browserless plan max (`timeout` query). Higher values return HTTP 400. */
export const BROWSERLESS_HOSTED_MAX_TIMEOUT_MS = 60_000;

type ReportPdfEnvironment = NodonEnvironment | `${NodonEnvironment}`;

const isLocalhostReportPdfEnvironment = (
  environment: ReportPdfEnvironment,
): boolean => environment === NodonEnvironment.Localhost;

/** PrintView waits this long for `[data-report-page]` nodes to mount (localhost). */
export const REPORT_PDF_PAGE_WAIT_MS = 10_000;

/** Test/prod page-mount wait — longer than the localhost 60s session budget. */
export const REPORT_PDF_PAGE_WAIT_REMOTE_MS = 45_000;

/**
 * PrintView waits this long for the stored cover-image fetch to settle after
 * pages have mounted. The Mapbox idle timer starts only after this.
 */
export const REPORT_PDF_COVER_IMAGE_WAIT_MS = 5_000;

/** Test/prod cover-image wait. */
export const REPORT_PDF_COVER_IMAGE_WAIT_REMOTE_MS = 10_000;

/**
 * PrintView waits this long for Mapbox idle (or a skipped cover) after the
 * cover-image fetch has settled. The 3D building often idles after the
 * transparent style; PrintView must not mark PDF-ready until that idle.
 * Keep this under {@link REPORT_PDF_READY_SELECTOR_TIMEOUT_MS} with room
 * for chart settle and unmount. Capped so the 60s hosted session can finish
 * after a longer Cloudflare `page.goto`.
 */
export const REPORT_PDF_COVER_MAP_WAIT_MS = 11_000;

/** Test/prod Mapbox idle wait. */
export const REPORT_PDF_COVER_MAP_WAIT_REMOTE_MS = 60_000;

/** Extra settle for charts after pages/cover waits. */
export const REPORT_PDF_CHART_SETTLE_MS = 1_500;

/**
 * PrintView waits this long for Map3d to unmount after the cover is frozen.
 */
export const REPORT_PDF_COVER_UNMOUNT_WAIT_MS = 2_000;

/**
 * Report view waits this long for the live cover map to expose a capture
 * handle before snapshotting for PDF export.
 */
export const REPORT_PDF_COVER_MAP_CAPTURE_WAIT_MS = 4_000;

/** Puppeteer `page.goto` timeout for the print URL (localhost).
 * Cloudflare quick-tunnel first byte is often slower than 15s from cloud Chrome.
 */
export const REPORT_PDF_GOTO_TIMEOUT_MS = 25_000;

/** Test/prod `page.goto` timeout. */
export const REPORT_PDF_GOTO_TIMEOUT_REMOTE_MS = 60_000;

/** Puppeteer wait for `html[data-report-pdf-ready]` (localhost). */
export const REPORT_PDF_READY_SELECTOR_TIMEOUT_MS = 30_000;

/** Test/prod ready-selector timeout. */
export const REPORT_PDF_READY_SELECTOR_TIMEOUT_REMOTE_MS = 90_000;

/** Max characters of a Browserless error body included in thrown errors. */
const BROWSERLESS_ERROR_BODY_SNIPPET_LENGTH = 300;

/** Extra `/function` attempts after a new Cloudflare tunnel's DNS is not visible yet. */
export const BROWSERLESS_PDF_MAX_ATTEMPTS = 3;

/** Delay between {@link BROWSERLESS_PDF_MAX_ATTEMPTS} retries. */
export const BROWSERLESS_PDF_RETRY_DELAY_MS = 3_000;

/**
 * PrintView page-mount wait for this environment.
 */
export const getReportPdfPageWaitMs = (
  environment: ReportPdfEnvironment,
): number =>
  isLocalhostReportPdfEnvironment(environment)
    ? REPORT_PDF_PAGE_WAIT_MS
    : REPORT_PDF_PAGE_WAIT_REMOTE_MS;

/**
 * PrintView cover-image wait for this environment.
 */
export const getReportPdfCoverImageWaitMs = (
  environment: ReportPdfEnvironment,
): number =>
  isLocalhostReportPdfEnvironment(environment)
    ? REPORT_PDF_COVER_IMAGE_WAIT_MS
    : REPORT_PDF_COVER_IMAGE_WAIT_REMOTE_MS;

/**
 * PrintView Mapbox idle wait for this environment.
 */
export const getReportPdfCoverMapWaitMs = (
  environment: ReportPdfEnvironment,
): number =>
  isLocalhostReportPdfEnvironment(environment)
    ? REPORT_PDF_COVER_MAP_WAIT_MS
    : REPORT_PDF_COVER_MAP_WAIT_REMOTE_MS;

/**
 * Puppeteer `page.goto` timeout for this environment.
 */
export const getReportPdfGotoTimeoutMs = (
  environment: ReportPdfEnvironment,
): number =>
  isLocalhostReportPdfEnvironment(environment)
    ? REPORT_PDF_GOTO_TIMEOUT_MS
    : REPORT_PDF_GOTO_TIMEOUT_REMOTE_MS;

/**
 * Puppeteer ready-selector timeout for this environment.
 */
export const getReportPdfReadySelectorTimeoutMs = (
  environment: ReportPdfEnvironment,
): number =>
  isLocalhostReportPdfEnvironment(environment)
    ? REPORT_PDF_READY_SELECTOR_TIMEOUT_MS
    : REPORT_PDF_READY_SELECTOR_TIMEOUT_REMOTE_MS;

/**
 * Worst-case PrintView wait before it may set `data-report-pdf-ready`.
 * Defaults to the localhost budget (fits the hosted 60s session).
 */
export const getReportPdfPrintWaitBudgetMs = (
  environment: ReportPdfEnvironment = NodonEnvironment.Localhost,
): number =>
  getReportPdfPageWaitMs(environment) +
  getReportPdfCoverImageWaitMs(environment) +
  getReportPdfCoverMapWaitMs(environment) +
  REPORT_PDF_CHART_SETTLE_MS +
  REPORT_PDF_COVER_UNMOUNT_WAIT_MS;

/**
 * Worst-case Puppeteer work before `page.pdf()` (navigation + ready selector).
 * Defaults to the localhost budget.
 */
export const getReportPdfPuppeteerWaitBudgetMs = (
  environment: ReportPdfEnvironment = NodonEnvironment.Localhost,
): number =>
  getReportPdfGotoTimeoutMs(environment) +
  getReportPdfReadySelectorTimeoutMs(environment);

/**
 * `/function` session length. Localhost uses the hosted plan max so the tunnel
 * session can finish. Test/prod request the Puppeteer budget and retry at the
 * account max if Browserless returns a timeout-limit 400.
 */
export const getReportPdfBrowserlessFunctionTimeoutMs = (
  environment: ReportPdfEnvironment = NodonEnvironment.Localhost,
): number =>
  isLocalhostReportPdfEnvironment(environment)
    ? BROWSERLESS_HOSTED_MAX_TIMEOUT_MS
    : getReportPdfPuppeteerWaitBudgetMs(environment);

const parsePositiveTimeoutMs = (raw: string): number | undefined => {
  const parsed = Number(raw.replace(/,/g, ''));
  if (!Number.isInteger(parsed) || parsed <= 0) return undefined;
  return parsed;
};

/**
 * Parses the account max from a Browserless 400 body.
 * Supports `configured TIMEOUT of 30000ms` and
 * `between 1 and 60,000 (your plan's maximum session time`.
 */
export const parseBrowserlessConfiguredTimeoutMs = (
  body: string,
): number | undefined => {
  const configured = body.match(/configured TIMEOUT of (\d+)ms/i)?.[1];
  if (configured) return parsePositiveTimeoutMs(configured);
  const between = body.match(/between 1 and ([\d,]+)/i)?.[1];
  if (between) return parsePositiveTimeoutMs(between);
  return undefined;
};

/**
 * Builds the Browserless `/function` URL.
 * Omits `timeout` unless a millisecond timeout is passed — values above the
 * account max are rejected with HTTP 400.
 */
export const buildBrowserlessFunctionUrl = (
  baseUrl: string,
  token: string,
  timeoutMs?: number,
): string => {
  const url = new URL(baseUrl);
  url.searchParams.set('token', token);
  if (timeoutMs === undefined) return url.toString();
  url.searchParams.set('timeout', String(timeoutMs));
  return url.toString();
};

/**
 * Strips the Browserless `token` query param so `/function` URLs can be logged.
 */
export const redactBrowserlessFunctionUrlForLog = (url: string): string => {
  try {
    const parsed = new URL(url);
    if (parsed.searchParams.has('token')) {
      parsed.searchParams.set('token', 'redacted');
    }
    return parsed.toString();
  } catch {
    return '[browserless-function-url]';
  }
};

/**
 * Formats a Browserless HTTP error for logs and Sentry.
 * Includes a short body snippet so 400s (e.g. out-of-range timeout) are
 * visible without opening Cloud Run logs.
 */
export const formatBrowserlessHttpError = (
  status: number,
  statusText: string,
  body: string,
): string => {
  const statusLabel = `Browserless error: ${status} ${statusText}`;
  const snippet = body
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, BROWSERLESS_ERROR_BODY_SNIPPET_LENGTH);
  if (!snippet) return statusLabel;
  return `${statusLabel}: ${snippet}`;
};

const getErrorCauseCode = (error: unknown): string | undefined => {
  if (!guards.is.error(error)) return undefined;
  const { cause } = error;
  if (!guards.is.defined(cause)) return undefined;
  if (
    guards.is.error(cause) &&
    'code' in cause &&
    guards.is.string(cause.code)
  ) {
    return cause.code;
  }
  if (guards.is.object(cause) && guards.is.string(cause.code)) {
    return cause.code;
  }
  return undefined;
};

/**
 * Formats a failed `fetch` to Browserless `/function` (connection refused, DNS).
 * `functionUrl` must not include the token query param.
 */
export const formatBrowserlessFetchError = (
  error: unknown,
  functionUrl: string,
): string => {
  const code = getErrorCauseCode(error);
  if (code === 'ECONNREFUSED') {
    return `Browserless is not reachable at ${functionUrl}.`;
  }
  if (guards.is.error(error))
    return `Browserless fetch failed: ${error.message}`;
  return 'Browserless fetch failed';
};

const RETRYABLE_BROWSERLESS_NAVIGATION_ERRORS = [
  'ERR_NAME_NOT_RESOLVED',
  'ERR_CONNECTION_RESET',
  'ERR_CONNECTION_CLOSED',
  'ERR_CONNECTION_REFUSED',
  'ERR_TUNNEL_CONNECTION_FAILED',
  'ERR_NETWORK_CHANGED',
  'Navigation timeout',
] as const;

/**
 * True when a `/function` HTTP error is a transient `page.goto` failure
 * (typical right after a Cloudflare quick tunnel hostname is created).
 */
export const shouldRetryBrowserlessPdfGenerate = (
  status: number,
  body: string,
): boolean => {
  if (status !== 400) return false;
  return RETRYABLE_BROWSERLESS_NAVIGATION_ERRORS.some((code) =>
    body.includes(code),
  );
};

/**
 * 16:9 print sheet (960×540 pt). Same aspect as the report canvas — A4
 * landscape is shorter and leaves letterbox bands.
 */
export const REPORT_PDF_PAGE_WIDTH_MM = 338.67;

/** Height of {@link REPORT_PDF_PAGE_WIDTH_MM} at 16:9. */
export const REPORT_PDF_PAGE_HEIGHT_MM = 190.5;

/** CSS px at 96dpi for {@link REPORT_PDF_PAGE_WIDTH_MM} (13.333in). */
export const REPORT_PDF_VIEWPORT_WIDTH_PX = 1280;

/** CSS px at 96dpi for {@link REPORT_PDF_PAGE_HEIGHT_MM} (7.5in). */
export const REPORT_PDF_VIEWPORT_HEIGHT_PX = 720;

/**
 * `@page` rule so Cmd+P and Browserless share 16:9 pages with no margin.
 * `page.pdf()` must set `preferCSSPageSize: true` or Puppeteer's `format`
 * overrides this rule.
 */
export const getReportPdfPrintCss = (): string =>
  `
@page { size: ${REPORT_PDF_PAGE_WIDTH_MM}mm ${REPORT_PDF_PAGE_HEIGHT_MM}mm; margin: 0; }
@media print {
  html, body { height: auto !important; overflow: visible !important; }
}
`.trim();

/** Query flag so PrintView skips Map3d; Browserless injects the client snapshot. */
export const REPORT_PDF_COVER_MAP_INJECTED_PARAM = 'coverMap';

/** Value of {@link REPORT_PDF_COVER_MAP_INJECTED_PARAM} when a client snapshot will be injected. */
export const REPORT_PDF_COVER_MAP_INJECTED_VALUE = 'injected';

/**
 * Marks a print URL so PrintView does not wait for Mapbox idle in headless
 * Chrome. The snapshot is passed separately in the Browserless function context.
 */
export const withInjectedCoverMapQuery = (printUrl: string): string => {
  const url = new URL(printUrl);
  url.searchParams.set(
    REPORT_PDF_COVER_MAP_INJECTED_PARAM,
    REPORT_PDF_COVER_MAP_INJECTED_VALUE,
  );
  return url.toString();
};

/**
 * True when the print session should skip live Map3d. Puppeteer injects the
 * client-captured cover snapshot after the print view marks PDF-ready.
 */
export const hasInjectedCoverMapQuery = (search: string): boolean => {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  );
  return (
    params.get(REPORT_PDF_COVER_MAP_INJECTED_PARAM) ===
    REPORT_PDF_COVER_MAP_INJECTED_VALUE
  );
};

/** Query key for the organization label printed in the report footer. */
export const REPORT_PDF_ORGANIZATION_NAME_PARAM = 'organizationName';

const MAX_REPORT_PDF_ORGANIZATION_NAME_LENGTH = 200;

const stripControlCharacters = (value: string): string =>
  [...value]
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code > 31 && code !== 127;
    })
    .join('');

/**
 * Footer label safe to put on a print URL.
 * Drops non-strings, control characters, and blank values. Longer names are truncated.
 */
export const parseReportPdfOrganizationName = (
  value: unknown,
): string | undefined => {
  if (!guards.is.string(value)) return undefined;
  const sanitized = stripControlCharacters(value).trim();
  if (!sanitized) return undefined;
  return sanitized.slice(0, MAX_REPORT_PDF_ORGANIZATION_NAME_LENGTH);
};

/**
 * Copies {@link organizationName} onto print/browse query params.
 * Returns {@link params} unchanged when the name is missing or blank.
 */
export const withReportPdfOrganizationName = (
  params: URLSearchParams | undefined,
  organizationName: string | undefined,
): URLSearchParams | undefined => {
  const name = parseReportPdfOrganizationName(organizationName);
  if (!name) return params;
  const next = new URLSearchParams(params);
  next.set(REPORT_PDF_ORGANIZATION_NAME_PARAM, name);
  return next;
};

/**
 * True when the requested `timeout` exceeded the Browserless account max.
 * Retry with {@link parseBrowserlessConfiguredTimeoutMs}.
 */
export const shouldRetryBrowserlessTimeoutLimit = (
  status: number,
  body: string,
  requestedTimeoutMs: number,
): boolean => {
  if (status !== 400) return false;
  const configured = parseBrowserlessConfiguredTimeoutMs(body);
  return configured !== undefined && configured < requestedTimeoutMs;
};
